import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Alert,
  useTheme,
} from "@mui/material";
import {
  Description as FileTextIcon,
  Check as CheckIcon,
  Close as XIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  AttachMoney as DollarSignIcon,
} from "@mui/icons-material";

interface PaystubRequest {
  id: number;
  employeeName: string;
  employeeId: string;
  period: string;
  amount: string;
  status: "Pending" | "Processed" | "Rejected";
  requestDate: string;
  dueDate: string;
  payDate: string;
}

const PaystubRequestsTab: React.FC = () => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState("");

  const [requests, setRequests] = useState<PaystubRequest[]>([
    {
      id: 1,
      employeeName: "Sarah Johnson",
      employeeId: "EMP001",
      period: "December 2024",
      amount: "$4,500.00",
      status: "Pending",
      requestDate: "2024-12-28",
      dueDate: "2024-12-31",
      payDate: "2024-12-31",
    },
    {
      id: 2,
      employeeName: "Mike Chen",
      employeeId: "EMP002",
      period: "December 2024",
      amount: "$5,200.00",
      status: "Pending",
      requestDate: "2024-12-27",
      dueDate: "2024-12-31",
      payDate: "2024-12-31",
    },
    {
      id: 3,
      employeeName: "Emily Davis",
      employeeId: "EMP003",
      period: "December 2024",
      amount: "$4,800.00",
      status: "Pending",
      requestDate: "2024-12-26",
      dueDate: "2024-12-31",
      payDate: "2024-12-31",
    },
    {
      id: 4,
      employeeName: "James Wilson",
      employeeId: "EMP004",
      period: "November 2024",
      amount: "$5,000.00",
      status: "Processed",
      requestDate: "2024-11-28",
      dueDate: "2024-11-30",
      payDate: "2024-11-30",
    },
    {
      id: 5,
      employeeName: "Lisa Brown",
      employeeId: "EMP005",
      period: "November 2024",
      amount: "$4,600.00",
      status: "Processed",
      requestDate: "2024-11-27",
      dueDate: "2024-11-30",
      payDate: "2024-11-30",
    },
  ]);

  const pendingCount = requests.filter(req => req.status === "Pending").length;

  const handleApprove = (id: number) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === id ? { ...req, status: "Processed" as const } : req
      )
    );
    setSuccessMessage("Paystub request approved successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReject = (id: number) => {
    setRequests(prev =>
      prev.map(req =>
        req.id === id ? { ...req, status: "Rejected" as const } : req
      )
    );
    setSuccessMessage("Paystub request rejected.");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleDownload = (id: number) => {
    console.log(`Downloading paystub for request ${id}`);
    setSuccessMessage("Paystub downloaded successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleBulkUpload = () => {
    console.log("Opening bulk upload dialog");
    setSuccessMessage("Bulk upload initiated!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Processed":
        return "success";
      case "Rejected":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
            <FileTextIcon color="primary" sx={{ mr: 1 }} />
            Paystub Requests
            {pendingCount > 0 && (
              <Chip
                label={pendingCount}
                color="error"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingCount} pending requests requiring attention
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleBulkUpload}
        >
          Bulk Upload
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Requests Grid */}
      <Grid container spacing={2}>
        {requests.map((request) => (
          <Grid item xs={12} md={6} key={request.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                border: request.status === "Pending" ? `2px solid ${theme.palette.warning.main}` : "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <DollarSignIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {request.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {request.employeeId}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={request.status}
                    color={getStatusColor(request.status) as any}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Period:</strong> {request.period}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Amount:</strong> {request.amount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Requested:</strong> {request.requestDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Pay Date:</strong> {request.payDate}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {request.status === "Pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => handleApprove(request.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<XIcon />}
                        onClick={() => handleReject(request.id)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {request.status === "Processed" && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(request.id)}
                    >
                      Download
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {requests.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <FileTextIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No paystub requests found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All paystub requests will appear here when submitted by employees.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PaystubRequestsTab;